import { useEffect, useState } from 'react'
import { onlineManager, useQueryClient } from '@tanstack/react-query'

export type NetworkQuality = 'ok' | 'unstable' | 'offline'

/**
 * Qualidade da conexão, derivada do próprio cache de queries.
 *
 * `unstable` é uma query **em nova tentativa**: `isFetching` com `failureCount > 0`. É a informação que
 * o React Query já mantém e que antes ficava invisível — o usuário só via a tela vazia ou um erro
 * definitivo, e concluía que a internet dele estava ruim.
 *
 * Note que `offline` depende de `navigator.onLine`, que **não** muda no `ERR_NETWORK_CHANGED` (a
 * interface do SO troca, o Chrome aborta o que está em voo, e `onLine` continua `true`). Esse caso
 * aparece como `unstable`, via `failureCount` — que é justamente o que o retry de transporte produz.
 */
export function useNetworkQuality(): NetworkQuality {
  const queryClient = useQueryClient()
  const [quality, setQuality] = useState<NetworkQuality>('ok')

  useEffect(() => {
    const evaluate = () => {
      if (!onlineManager.isOnline()) {
        setQuality('offline')
        return
      }

      const isRetrying = queryClient
        .getQueryCache()
        .getAll()
        .some((query) => query.state.fetchStatus === 'fetching' && query.state.fetchFailureCount > 0)

      setQuality(isRetrying ? 'unstable' : 'ok')
    }

    evaluate()

    const unsubscribeCache = queryClient.getQueryCache().subscribe(evaluate)
    const unsubscribeOnline = onlineManager.subscribe(evaluate)

    return () => {
      unsubscribeCache()
      unsubscribeOnline()
    }
  }, [queryClient])

  return quality
}
